import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import {
  formatPointTooltip,
  linePointsToUplotData,
  nearestFinitePoint,
  placePointTooltip,
  positionPlaybackMarker,
  scalesDifferFromFullRange,
  timeFromChartClientX,
} from "./metricTimeSeriesChartUtils";
import { loadUplot } from "./uplotLoader";
import { contactMarkerAtPoint, markerColorForLandmark } from "../../../util/floorContacts";

function createPlaybackMarkerPlugin(getPlayheadTime, thicknessDenom, markerRef) {
  const reposition = (u) =>
    positionPlaybackMarker(u, markerRef.current, getPlayheadTime(), thicknessDenom);

  return {
    hooks: {
      ready: [
        (u) => {
          const el = document.createElement("div");
          el.className = "metric-timeseries-playhead";
          el.style.cssText =
            "position:absolute;top:0;left:0;height:100%;background:#111111;pointer-events:none;will-change:transform;";
          u.over.appendChild(el);
          markerRef.current = el;
          reposition(u);
        },
      ],
      setScale: [reposition],
      setSize: [reposition],
    },
  };
}

function drawCircle(ctx, x, y, radius, color, hollow) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  if (hollow) {
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
    return;
  }
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = "#111111";
  ctx.lineWidth = 1;
  ctx.stroke();
}

function drawUpTriangle(ctx, x, y, size, color, hollow) {
  const h = size;
  ctx.beginPath();
  ctx.moveTo(x, y - h);
  ctx.lineTo(x + h * 0.9, y + h * 0.7);
  ctx.lineTo(x - h * 0.9, y + h * 0.7);
  ctx.closePath();
  if (hollow) {
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
    return;
  }
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = "#111111";
  ctx.lineWidth = 1;
  ctx.stroke();
}

/**
 * Draw static contact markers onto the uPlot canvas.
 * Markers are static (unlike the DOM playhead), so a draw hook is enough —
 * toggles call plot.redraw() without recreating the instance.
 */
export function createContactMarkersPlugin(getMarkers) {
  return {
    hooks: {
      draw: [
        (u) => {
          const markers = getMarkers() || [];
          if (!markers.length) return;
          const ctx = u.ctx;
          const { left, top, width, height } = u.bbox;
          ctx.save();
          ctx.beginPath();
          ctx.rect(left, top, width, height);
          ctx.clip();

          for (let i = 0; i < markers.length; i += 1) {
            const m = markers[i];
            if (!Number.isFinite(m.t) || !Number.isFinite(m.y)) continue;
            const x = u.valToPos(m.t, "x", true);
            const y = u.valToPos(m.y, "y", true);
            if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
            const color = markerColorForLandmark(m.landmark);
            const hollow = m.source === "user";
            if (hollow) continue;
            if (m.event === "off") {
              drawUpTriangle(ctx, x, y, 6, color, false);
            } else {
              drawCircle(ctx, x, y, 5, color, false);
            }
          }
          for (let i = 0; i < markers.length; i += 1) {
            const m = markers[i];
            if (m.source !== "user") continue;
            if (!Number.isFinite(m.t) || !Number.isFinite(m.y)) continue;
            const x = u.valToPos(m.t, "x", true);
            const y = u.valToPos(m.y, "y", true);
            if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
            const color = markerColorForLandmark(m.landmark);
            if (m.event === "off") {
              drawUpTriangle(ctx, x, y, 6, color, true);
            } else {
              drawCircle(ctx, x, y, 5, color, true);
            }
          }
          ctx.restore();
        },
      ],
    },
  };
}

/**
 * DOM tooltip next to the hovered sample. A canvas legend would sit far from
 * the curve; this element tracks the point itself.
 */
export function createPointValueTooltipPlugin(axisTitle) {
  let tip = null;
  const hide = () => {
    if (tip) tip.hidden = true;
  };
  return {
    hooks: {
      ready: [
        (u) => {
          tip = document.createElement("div");
          tip.className = "metric-timeseries-point-tip";
          tip.setAttribute("role", "tooltip");
          tip.hidden = true;
          // Inline so the popup cannot take the hit target and dismiss itself
          // before the stylesheet arrives.
          tip.style.pointerEvents = "none";
          tip.style.position = "absolute";
          tip.style.zIndex = "3";
          u.over.appendChild(tip);
        },
      ],
      setCursor: [
        (u) => {
          if (!tip) return;
          const idx = u.cursor ? u.cursor.idx : null;
          const point = nearestFinitePoint(u.data && u.data[0], u.data && u.data[1], idx);
          if (!point) {
            hide();
            return;
          }
          const px = u.valToPos(point.x == null ? 0 : point.x, "x");
          const py = u.valToPos(point.y, "y");
          if (!Number.isFinite(px) || !Number.isFinite(py)) {
            hide();
            return;
          }
          tip.textContent = formatPointTooltip(point.y, axisTitle);
          tip.hidden = false;
          const place = placePointTooltip(
            px,
            py,
            tip.offsetWidth,
            tip.offsetHeight,
            u.over.clientWidth,
            u.over.clientHeight
          );
          tip.style.transform = `translate(${place.left}px, ${place.top}px)`;
        },
      ],
    },
  };
}

function linePointsYRange(linePoints) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < linePoints.length; i += 1) {
    const y = linePoints[i]?.y;
    if (typeof y !== "number" || !Number.isFinite(y)) continue;
    if (y < min) min = y;
    if (y > max) max = y;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (min === max) {
    const pad = Math.max(1, Math.abs(min) * 0.05);
    return { min: min - pad, max: max + pad };
  }
  const pad = (max - min) * 0.05;
  return { min: min - pad, max: max + pad };
}

function writeUserMarkerPoints(plot, node, markers) {
  if (!node || !plot || typeof plot.valToPos !== "function") return;
  const points = [];
  for (let i = 0; i < (markers || []).length; i += 1) {
    const marker = markers[i];
    if (!marker || !marker.id) continue;
    const px = plot.valToPos(marker.t, "x");
    const py = plot.valToPos(marker.y, "y");
    if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
    points.push(`${Math.round(px)},${Math.round(py)}`);
  }
  node.setAttribute("data-user-marker-points", points.join(" "));
}

function MetricTimeSeriesChart({
  linePoints = [],
  playheadTime = 0,
  seriesMinX = 0,
  seriesMaxX = 0,
  axisYTitle = "Degrees",
  onSeek,
  onAnnotateClick,
  inComparePage = false,
  height = 280,
  contactMarkers = [],
  annotateMode = false,
  annotateType = null,
  onRemoveAnnotation,
  stepped = false,
}) {
  const containerRef = useRef(null);
  const plotRef = useRef(null);
  const markerRef = useRef(null);
  const contactMarkersRef = useRef(contactMarkers);
  const fullRangeRef = useRef({ min: 0, max: 0 });
  const yRangeRef = useRef(null);
  const dataRef = useRef(null);
  const onSeekRef = useRef(onSeek);
  const onAnnotateClickRef = useRef(onAnnotateClick);
  const annotateModeRef = useRef(annotateMode);
  const annotateTypeRef = useRef(annotateType);
  const onRemoveAnnotationRef = useRef(onRemoveAnnotation);
  const playheadTimeRef = useRef(playheadTime);
  const isZoomedRef = useRef(false);
  const [uPlotLib, setUPlotLib] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  onSeekRef.current = onSeek;
  onAnnotateClickRef.current = onAnnotateClick;
  annotateModeRef.current = annotateMode;
  annotateTypeRef.current = annotateType;
  onRemoveAnnotationRef.current = onRemoveAnnotation;
  playheadTimeRef.current = playheadTime;
  contactMarkersRef.current = contactMarkers;
  isZoomedRef.current = isZoomed;

  const data = useMemo(() => linePointsToUplotData(linePoints), [linePoints]);
  const yRange = useMemo(() => linePointsYRange(linePoints), [linePoints]);
  const hasData =
    data[0].length > 0 &&
    data[1].some((y) => typeof y === "number" && Number.isFinite(y));
  const thicknessDenom = inComparePage ? 500 : 800;

  fullRangeRef.current = { min: seriesMinX, max: seriesMaxX };
  yRangeRef.current = yRange;
  dataRef.current = data;

  const applyFullScales = useCallback((plot) => {
    if (!plot) return;
    const { min, max } = fullRangeRef.current;
    if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
      plot.setScale("x", { min, max });
    }
    const yr = yRangeRef.current;
    if (yr) {
      plot.setScale("y", { min: yr.min, max: yr.max });
    }
  }, []);

  const syncZoomState = useCallback((u) => {
    const { min, max } = fullRangeRef.current;
    const zoomed = scalesDifferFromFullRange(u.scales.x, min, max);
    isZoomedRef.current = zoomed;
    setIsZoomed(zoomed);
  }, []);

  const resetZoom = useCallback(() => {
    const plot = plotRef.current;
    if (!plot) return;
    applyFullScales(plot);
    setIsZoomed(false);
    isZoomedRef.current = false;
    positionPlaybackMarker(
      plot,
      markerRef.current,
      playheadTimeRef.current,
      thicknessDenom
    );
  }, [thicknessDenom, applyFullScales]);

  useEffect(() => {
    let cancelled = false;
    loadUplot().then((lib) => {
      if (!cancelled) setUPlotLib(() => lib);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Create the plot once per mount / series availability. Size and data updates
  // use setSize / setData — recreating on every height or yRange change left
  // most charts blank when several mounted together.
  useEffect(() => {
    if (!uPlotLib || !containerRef.current || !hasData) {
      return undefined;
    }

    const container = containerRef.current;
    const width = container.clientWidth || 800;
    const initialHeight = Math.max(container.clientHeight || 0, height || 280);
    const initialData = dataRef.current;

    const opts = {
      width,
      height: initialHeight,
      pxAlign: 0,
      scales: { x: { time: false } },
      axes: [
        {
          label: "Time(s)",
          stroke: "#666666",
          grid: { show: true, stroke: "rgba(0,0,0,0.08)" },
          ticks: { stroke: "#888888" },
        },
        {
          label: axisYTitle,
          stroke: "#666666",
          grid: { show: true, stroke: "rgba(0,0,0,0.08)" },
          ticks: { stroke: "#888888" },
        },
      ],
      series: [
        {},
        {
          stroke: "#63C7CF",
          width: 2,
          // Null samples (contact/off columns, dropped height frames) stay gaps.
          spanGaps: false,
          // State channels hold their value until the next sample.
          ...(stepped ? { paths: uPlotLib.paths.stepped({ align: 1 }) } : {}),
        },
      ],
      cursor: {
        // Drag scrubs the trial. uPlot's drag zoom stays off.
        drag: { x: false, y: false, setScale: false },
        focus: { prox: 16 },
      },
      select: { show: false },
      plugins: [
        createPlaybackMarkerPlugin(
          () => playheadTimeRef.current,
          thicknessDenom,
          markerRef
        ),
        createContactMarkersPlugin(() => contactMarkersRef.current),
        createPointValueTooltipPlugin(axisYTitle),
      ],
      hooks: {
        ready: [
          (u) => {
            const markerHit = (clientX, clientY) => {
              if (!annotateModeRef.current || typeof onRemoveAnnotationRef.current !== 'function') {
                return null;
              }
              const rect = u.over.getBoundingClientRect();
              return contactMarkerAtPoint(
                (contactMarkersRef.current || []).map((marker) => {
                  if (!marker || typeof u.valToPos !== 'function') return marker;
                  return {
                    ...marker,
                    px: u.valToPos(marker.t, 'x'),
                    py: u.valToPos(marker.y, 'y'),
                  };
                }),
                clientX - rect.left,
                clientY - rect.top,
                12,
                annotateTypeRef.current
              );
            };
            const seekAt = (clientX) => {
              const time = timeFromChartClientX(u, clientX);
              const cb = onSeekRef.current;
              if (cb && time != null) cb(time);
              return time;
            };
            // A press that stays inside this radius is a click and may place
            // one mark. Anything farther is a playback scrub and places none.
            const clickSlopPx = 5;
            let scrubbing = false;
            let dragged = false;
            let originX = 0;
            let originY = 0;
            const onPointerDown = (event) => {
              if (event.button !== 0) return;
              const hit = markerHit(event.clientX, event.clientY);
              if (hit && hit.id) {
                onRemoveAnnotationRef.current(hit.id);
                return;
              }
              scrubbing = true;
              dragged = false;
              originX = event.clientX;
              originY = event.clientY;
              u.over.classList.add("is-scrubbing");
              if (typeof u.over.setPointerCapture === "function") {
                try {
                  u.over.setPointerCapture(event.pointerId);
                } catch (_) {
                  /* capture can fail if the pointer already ended */
                }
              }
              seekAt(event.clientX);
            };
            const onPointerMove = (event) => {
              if (!scrubbing) return;
              const dx = event.clientX - originX;
              const dy = event.clientY - originY;
              if (dx * dx + dy * dy > clickSlopPx * clickSlopPx) dragged = true;
              seekAt(event.clientX);
            };
            const endScrub = (event) => {
              if (!scrubbing) return;
              const wasDrag = dragged;
              scrubbing = false;
              dragged = false;
              u.over.classList.remove("is-scrubbing");
              const clientX = event && Number.isFinite(event.clientX) ? event.clientX : originX;
              const time = seekAt(clientX);
              if (wasDrag || !annotateModeRef.current) return;
              const place = onAnnotateClickRef.current;
              if (typeof place === 'function' && time != null) place(time);
            };
            u.over.addEventListener("pointerdown", onPointerDown);
            u.over.addEventListener("pointermove", onPointerMove);
            u.over.addEventListener("pointerup", endScrub);
            u.over.addEventListener("pointercancel", endScrub);
            u._evovisionScrubCleanup = () => {
              u.over.removeEventListener("pointerdown", onPointerDown);
              u.over.removeEventListener("pointermove", onPointerMove);
              u.over.removeEventListener("pointerup", endScrub);
              u.over.removeEventListener("pointercancel", endScrub);
            };
          },
        ],
        setScale: [
          (u) => {
            syncZoomState(u);
          },
        ],
        destroy: [
          (u) => {
            if (typeof u._evovisionScrubCleanup === "function") {
              u._evovisionScrubCleanup();
            }
          },
        ],
      },
    };

    const plot = new uPlotLib(opts, initialData, container);
    plotRef.current = plot;
    container.__evovisionPlot = plot;
    applyFullScales(plot);
    syncZoomState(plot);

    const resize = () => {
      if (!containerRef.current || !plotRef.current) return;
      const nextWidth = containerRef.current.clientWidth;
      const nextHeight = containerRef.current.clientHeight;
      if (nextWidth > 0 && nextHeight > 0) {
        plotRef.current.setSize({ width: nextWidth, height: nextHeight });
        if (!isZoomedRef.current) {
          applyFullScales(plotRef.current);
        }
        writeUserMarkerPoints(
          plotRef.current,
          containerRef.current,
          contactMarkersRef.current
        );
      }
    };

    // Defer one resize after layout so flex height is final.
    const raf = window.requestAnimationFrame(resize);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(resize)
        : null;
    if (ro) ro.observe(container);
    window.addEventListener("resize", resize);

    // Off-screen charts can mount with a usable size but never paint the
    // series; when they enter the viewport, push data + scales again.
    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              const plotNow = plotRef.current;
              if (!plotNow) return;
              for (let i = 0; i < entries.length; i += 1) {
                if (!entries[i].isIntersecting) continue;
                const latest = dataRef.current;
                if (latest && latest[0]?.length) {
                  plotNow.setData(latest);
                  if (!isZoomedRef.current) applyFullScales(plotNow);
                  else if (typeof plotNow.redraw === "function") plotNow.redraw();
                }
              }
            },
            { root: null, threshold: 0.05 }
          )
        : null;
    if (io) io.observe(container);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      if (ro) ro.disconnect();
      if (io) io.disconnect();
      if (container.__evovisionPlot === plot) {
        delete container.__evovisionPlot;
      }
      plot.destroy();
      plotRef.current = null;
      markerRef.current = null;
    };
  }, [
    uPlotLib,
    axisYTitle,
    hasData,
    height,
    thicknessDenom,
    syncZoomState,
    applyFullScales,
    stepped,
  ]);

  useEffect(() => {
    const plot = plotRef.current;
    if (!plot || !hasData) return;
    plot.setData(data);
    if (!isZoomedRef.current) {
      applyFullScales(plot);
    }
    positionPlaybackMarker(
      plot,
      markerRef.current,
      playheadTimeRef.current,
      thicknessDenom
    );
  }, [data, hasData, thicknessDenom, applyFullScales]);

  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    if (!isZoomedRef.current) {
      applyFullScales(plot);
      positionPlaybackMarker(
        plot,
        markerRef.current,
        playheadTimeRef.current,
        thicknessDenom
      );
    }
  }, [seriesMinX, seriesMaxX, yRange, thicknessDenom, applyFullScales]);

  useEffect(() => {
    positionPlaybackMarker(
      plotRef.current,
      markerRef.current,
      playheadTime,
      thicknessDenom
    );
  }, [playheadTime, thicknessDenom]);

  // Toggle / filter changes: redraw canvas without recreating the plot.
  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    contactMarkersRef.current = contactMarkers;
    if (typeof plot.redraw === "function") {
      plot.redraw();
    }
    writeUserMarkerPoints(plot, containerRef.current, contactMarkers);
  }, [contactMarkers]);

  return (
    <div className="metric-timeseries-chart-wrap">
      {isZoomed ? (
        <button
          type="button"
          className="chart-reset-zoom-btn"
          onClick={resetZoom}
          aria-label="Reset zoom to full timeline"
        >
          Reset zoom
        </button>
      ) : null}
      <div
        className="metric-timeseries-chart"
        ref={containerRef}
        style={{ width: "100%", height: "100%", minHeight: height }}
        data-interaction="scrub"
        data-has-data={hasData ? "true" : "false"}
        data-stepped={stepped ? "true" : "false"}
        data-playhead-time={playheadTime}
        data-series-min-x={seriesMinX}
        data-series-max-x={seriesMaxX}
        data-zoomed={isZoomed ? "true" : "false"}
        data-contact-marker-count={contactMarkers.length}
        data-contact-marker-sides={(contactMarkers || [])
          .map((m) => m.side || '')
          .join(',')}
        data-contact-marker-times={(contactMarkers || [])
          .map((m) => (typeof m.t === 'number' ? String(m.t) : ''))
          .join(',')}
        data-line-first-x={
          linePoints && linePoints.length ? String(linePoints[0].x) : ''
        }
      />
    </div>
  );
}

export default MetricTimeSeriesChart;
